/**
 * AI CONTEXT: mobile-first - Account confirmation page
 * 
 * This component handles email verification with:
 * - Mobile-first responsive design
 * - Clear instructions for users
 * - Error handling and retry functionality
 * - Touch-friendly interface elements
 * - Integration with AWS Cognito
 * - Shipping address and SMS consent persistence after confirmation
 * 
 * Architecture decisions:
 * - Use location state to get email, shipping address, and SMS consent from registration
 * - Provide clear user feedback
 * - Include resend functionality
 * - Follow mobile-first design principles
 * - Save shipping address and SMS consent to DynamoDB after successful confirmation
 * 
 * Future considerations:
 * - Add SMS verification as alternative
 * - Implement auto-refresh for confirmation status
 * - Add progress indicators
 * - Consider multi-step verification flow
 */

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, ArrowLeft, RefreshCw } from 'lucide-react';
import { updateMyProfile, sendWelcomeSMS, isApiConfigured, recordReferral } from '../services/apiClient';
import { setOrCreateMeta } from '../utils/seo';

const ConfirmAccount = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { confirmAccountAndSignIn, resendConfirmationCode, loading } = useAuth();
  
  const email = location.state?.email || '';
  const password = location.state?.password || '';
  const phone = location.state?.phone || '';
  const referralCode = location.state?.referralCode || '';
  const shippingAddress = location.state?.shippingAddress || null;
  const smsConsent = location.state?.smsConsent || false;
  
  const [confirmationCode, setConfirmationCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    setOrCreateMeta('meta[name="robots"]', { name: 'robots' }, 'content', 'noindex,nofollow');
  }, []);

  // AI CONTEXT: ux - Handle confirmation code input
  // Reasoning: Provide immediate feedback and clear instructions
  // Constraints: Must handle various input formats
  // Future: Consider implementing auto-focus and paste handling

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setConfirmationCode(value);
    setError('');
  };

  // AI CONTEXT: security - Handle account confirmation with shipping address and SMS consent persistence
  // Reasoning: Verify email address for security and legal compliance, then save shipping address and SMS consent
  // Constraints: Must handle various confirmation scenarios and profile persistence
  // Future: Consider implementing additional verification methods

  const handleConfirm = async (e) => {
    e.preventDefault();
    
    if (!confirmationCode.trim()) {
      setError('Please enter the confirmation code');
      return;
    }
    
    if (confirmationCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }
    
    try {
      // Use the new method that confirms and automatically signs in the user
      const result = await confirmAccountAndSignIn(email, confirmationCode, password);
      
      if (result.success) {
        // Record referral if we have a code (retry from registration; backend is idempotent)
        if (referralCode && email && isApiConfigured()) {
          try {
            await recordReferral(referralCode.trim(), email);
          } catch (err) {
            console.warn('Failed to record referral after confirmation:', err);
          }
        }

        // Send welcome SMS if API is configured and phone number is provided
        if (phone && isApiConfigured()) {
          try {
            await sendWelcomeSMS(phone);
            console.log('Welcome SMS sent successfully after confirmation');
          } catch (error) {
            console.warn('Failed to send welcome SMS after confirmation:', error);
            // Don't fail confirmation if SMS send fails
          }
        }
        
        // Save shipping address and SMS consent to DynamoDB if provided during registration
        if (shippingAddress || smsConsent !== undefined) {
          try {
            const profileData = {};
            
            if (shippingAddress) {
              profileData.shippingAddress = shippingAddress;
            }
            
            if (smsConsent !== undefined) {
              profileData.smsConsent = smsConsent;
            }
            
            await updateMyProfile(profileData);
            console.log('Profile data saved successfully after confirmation');
          } catch (error) {
            console.warn('Failed to save profile data after confirmation:', error);
            // Don't fail confirmation if profile save fails
          }
        }
        
        setSuccess(true);
        
        // Navigate based on whether user was automatically signed in
        if (result.requiresSignIn) {
          // User needs to manually sign in
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        } else {
          // User is automatically signed in, go to dashboard
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        }
      } else {
        setError(result.message || 'Invalid confirmation code');
      }
    } catch (error) {
      console.error('Confirmation error:', error);
      setError('An error occurred. Please try again.');
    }
  };

  // AI CONTEXT: ux - Handle resend confirmation code
  // Reasoning: Users may not receive or lose confirmation emails
  // Constraints: Rate limiting applies
  // Future: Consider SMS as backup verification method

  const handleResend = async () => {
    try {
      setResendLoading(true);
      const result = await resendConfirmationCode(email);
      
      if (result.success) {
        setError('');
      } else {
        setError(result.message || 'Failed to resend code');
      }
    } catch (error) {
      console.error('Resend error:', error);
      setError('Failed to resend confirmation code');
    } finally {
      setResendLoading(false);
    }
  };

  // AI CONTEXT: ux - Handle invalid request
  // Reasoning: Provide clear guidance for invalid access
  // Constraints: Must redirect to proper registration flow
  // Future: Add analytics for invalid access attempts

  if (!email) {
    return (
      <div className="min-h-screen bg-theme-black flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md text-center">
          <h1 className="text-2xl font-bold text-theme-silver mb-4">Invalid Request</h1>
          <p className="text-theme-silver-light mb-6">
            Please complete the registration process to confirm your account.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center text-custom-orange hover:text-custom-orange/80 font-medium"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Registration
          </Link>
        </div>
      </div>
    );
  }

  // AI CONTEXT: ux - Show confirmation success
  // Reasoning: Provide clear confirmation of successful verification
  // Constraints: Must redirect after confirmation
  // Future: Add verification timestamp and details

  if (success) {
    return (
      <div className="min-h-screen bg-theme-black flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md text-center">
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-theme-silver mb-2">Account Confirmed!</h1>
            <p className="text-theme-silver-light">
              Your account has been successfully confirmed. You can now sign in.
            </p>
            {shippingAddress && (
              <p className="text-sm text-theme-silver-light mt-2">
                Your shipping address has been saved to your profile.
              </p>
            )}
          </div>
          <div className="animate-pulse">
            <p className="text-sm text-theme-silver-light">Redirecting to login...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-theme-black flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-custom-orange rounded-full flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-theme-silver mb-2">Confirm Your Account</h1>
          <p className="text-theme-silver-light">
            We've sent a confirmation code to{' '}
            <span className="text-custom-orange font-medium">{email}</span>
          </p>
        </div>

        {/* Confirmation Form */}
        <form onSubmit={handleConfirm} className="space-y-6">
          {/* Confirmation Code Input */}
          <div>
            <label htmlFor="confirmationCode" className="block text-sm font-medium text-theme-silver mb-2">
              Confirmation Code
            </label>
            <input
              type="text"
              id="confirmationCode"
              value={confirmationCode}
              onChange={handleCodeChange}
              className="w-full px-4 py-3 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50 text-center text-2xl tracking-widest"
              placeholder="000000"
              maxLength={6}
              autoComplete="one-time-code"
            />
            <p className="mt-2 text-xs text-theme-silver-light text-center">
              Enter the 6-digit code from your email
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 glass-card-dark border-red-500/30 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || confirmationCode.length !== 6}
            className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Confirming...' : 'Confirm Account'}
          </button>
        </form>

        {/* Resend Code */}
        <div className="mt-6 text-center">
          <p className="text-theme-silver-light mb-4">Didn't receive the code?</p>
          <button
            onClick={handleResend}
            disabled={resendLoading}
            className="inline-flex items-center text-custom-orange hover:text-custom-orange/80 font-medium disabled:text-theme-silver-light"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${resendLoading ? 'animate-spin' : ''}`} />
            {resendLoading ? 'Sending...' : 'Resend Code'}
          </button>
        </div>

        {/* Back to Login */}
        <div className="mt-8 text-center">
          <p className="text-theme-silver-light">
            Already confirmed?{' '}
            <Link to="/login" className="text-custom-orange hover:text-custom-orange/80 font-medium">
              Sign In
            </Link>
          </p>
        </div>

        {/* Help Section */}
        <div className="mt-8 glass-card-dark p-4 rounded-lg">
          <h3 className="text-sm font-medium text-theme-silver mb-2">Need Help?</h3>
          <ul className="text-xs text-theme-silver-light space-y-1">
            <li>• Check your spam/junk folder</li>
            <li>• Make sure you entered the correct email address</li>
            <li>• Wait a few minutes before requesting a new code</li>
            <li>• Contact support if you continue to have issues</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ConfirmAccount; 